import { v4 as uuidv4 } from 'uuid'
import { Token } from '../../domain/models/Token'
import { ITokenRepository, TokenData } from '../../domain/repositories/ITokenRepository'
import { tokenStorage } from '../persistence/TokenStorage'

/**
 * Implementation of ITokenRepository using TokenStorage
 */
export class TokenRepository implements ITokenRepository {
  private readonly TOKEN_PREFIX = 'auth_token_'
  private readonly USER_TOKENS_PREFIX = 'user_tokens_'
  private readonly SESSION_PREFIX = 'session_'

  /**
   * Save user tokens
   */
  async saveUserTokens(
    userId: string,
    accessToken: Token,
    refreshToken: Token,
  ): Promise<void> {
    // Create new session
    const sessionId = uuidv4()

    // Save tokens
    this.saveToken(userId, accessToken, sessionId)
    this.saveToken(userId, refreshToken, sessionId)

    // Update user tokens list
    this.updateUserTokensList(
      userId,
      accessToken.value,
      refreshToken.value,
      sessionId,
    )

    // Save session info
    this.saveSessionInfo(sessionId, userId)
  }

  /**
   * Update access token
   */
  async updateAccessToken(userId: string, accessToken: Token): Promise<void> {
    // Get user tokens
    const userTokens = this.getUserTokensList(userId)
    if (!userTokens) return

    // Get session ID for the current tokens
    const sessionId = userTokens.sessionId

    // Save new access token
    this.saveToken(userId, accessToken, sessionId)

    // Update tokens list
    userTokens.accessToken = accessToken.value
    this.setUserTokensList(userId, userTokens)

    // Update session last used time
    this.updateSessionLastUsed(sessionId)
  }

  /**
   * Revoke all tokens for user
   */
  async revokeAllUserTokens(userId: string): Promise<void> {
    const userTokens = this.getUserTokensList(userId)
    if (!userTokens) return

    // Revoke current tokens
    if (userTokens.accessToken) {
      this.revokeToken(userTokens.accessToken)
    }

    if (userTokens.refreshToken) {
      this.revokeToken(userTokens.refreshToken)
    }

    // Remove session
    if (userTokens.sessionId) {
      this.removeSession(userTokens.sessionId)
    }

    // Clear user tokens list
    tokenStorage.removeItem(this.getUserTokensKey(userId))
  }

  /**
   * Find tokens by user ID
   */
  async findTokensByUserId(userId: string): Promise<{
    accessTokenData: TokenData | null
    refreshTokenData: TokenData | null
  }> {
    const userTokens = this.getUserTokensList(userId)

    if (!userTokens) {
      return { accessTokenData: null, refreshTokenData: null }
    }

    // Get tokens data
    const accessTokenData = userTokens.accessToken
      ? this.getTokenData(this.getTokenKey(userTokens.accessToken))
      : null

    const refreshTokenData = userTokens.refreshToken
      ? this.getTokenData(this.getTokenKey(userTokens.refreshToken))
      : null

    return { accessTokenData, refreshTokenData }
  }

  /**
   * Revoke specific token
   */
  private revokeToken(tokenValue: string): void {
    const tokenKey = this.getTokenKey(tokenValue)
    tokenStorage.removeItem(tokenKey)
  }

  /**
   * Save token to storage
   */
  private saveToken(userId: string, token: Token, sessionId: string): void {
    const tokenKey = this.getTokenKey(token.value)
    const tokenData = {
      value: token.value,
      type: token.type,
      userId,
      sessionId,
      expiresAt: token.expiresAt?.toISOString(),
    }

    tokenStorage.setItem(tokenKey, JSON.stringify(tokenData), {
      secure: false,
    })
  }

  /**
   * Get token data from storage
   */
  private getTokenData(tokenKey: string): TokenData | null {
    const tokenJson = tokenStorage.getItem(tokenKey)
    if (!tokenJson) return null

    try {
      return JSON.parse(tokenJson) as TokenData
    } catch (e) {
      console.error('Failed to parse token data', e)
      return null
    }
  }

  /**
   * Get token key for storage
   */
  private getTokenKey(tokenValue: string): string {
    return `${this.TOKEN_PREFIX}${tokenValue}`
  }

  /**
   * Get user tokens list key
   */
  private getUserTokensKey(userId: string): string {
    return `${this.USER_TOKENS_PREFIX}${userId}`
  }

  /**
   * Get session key
   */
  private getSessionKey(sessionId: string): string {
    return `${this.SESSION_PREFIX}${sessionId}`
  }

  /**
   * Get user tokens list from storage
   */
  private getUserTokensList(userId: string): UserTokensList | null {
    const key = this.getUserTokensKey(userId)
    const json = tokenStorage.getItem(key)

    if (!json) return null

    try {
      return JSON.parse(json) as UserTokensList
    } catch (e) {
      console.error('Failed to parse user tokens list', e)
      return null
    }
  }

  /**
   * Set user tokens list in storage
   */
  private setUserTokensList(userId: string, tokens: UserTokensList): void {
    const key = this.getUserTokensKey(userId)
    tokenStorage.setItem(key, JSON.stringify(tokens))
  }

  /**
   * Update user tokens list
   */
  private updateUserTokensList(
    userId: string,
    accessToken: string,
    refreshToken: string,
    sessionId: string,
  ): void {
    const tokens: UserTokensList = {
      accessToken,
      refreshToken,
      sessionId,
    }

    this.setUserTokensList(userId, tokens)
  }

  /**
   * Save session information
   */
  private saveSessionInfo(sessionId: string, userId: string): void {
    const sessionKey = this.getSessionKey(sessionId)
    const now = new Date().toISOString()

    const sessionData: SessionData = {
      id: sessionId,
      userId,
      createdAt: now,
      lastUsedAt: now,
    }

    tokenStorage.setItem(sessionKey, JSON.stringify(sessionData))
  }

  /**
   * Update session last used timestamp
   */
  private updateSessionLastUsed(sessionId: string): void {
    const sessionKey = this.getSessionKey(sessionId)
    const sessionData = this.getSessionData(sessionKey)

    if (sessionData) {
      sessionData.lastUsedAt = new Date().toISOString()
      tokenStorage.setItem(sessionKey, JSON.stringify(sessionData))
    }
  }

  /**
   * Get session data
   */
  private getSessionData(sessionKey: string): SessionData | null {
    const json = tokenStorage.getItem(sessionKey)
    if (!json) return null

    try {
      return JSON.parse(json) as SessionData
    } catch (e) {
      console.error('Failed to parse session data', e)
      return null
    }
  }

  /**
   * Remove session
   */
  private removeSession(sessionId: string): void {
    const sessionKey = this.getSessionKey(sessionId)
    tokenStorage.removeItem(sessionKey)
  }
}

/**
 * User tokens list
 */
interface UserTokensList {
  accessToken: string
  refreshToken: string
  sessionId: string
}

/**
 * Session data
 */
interface SessionData {
  id: string
  userId: string
  createdAt: string
  lastUsedAt: string
}

export const tokenRepository = new TokenRepository()
